import { NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import React from 'react';

import HeadMeta from '#components/HeadMeta';
import MainLayout from '#layout/MainLayout';

export interface TermsConditionsProps {}

const TermsConditions: NextPage<TermsConditionsProps> = ({}) => {
  const { t } = useTranslation('terms');

  return (
    <MainLayout footerVisible>
      <HeadMeta title={t('pageTitle.terms')} />

      <div className="terms px-24 py-32">
        <p>
          <span>THESE TERMS OF USE INCLUDE A BINDING ARBITRATION PROVISION AND CLASS ACTION WAIVER</span>
        </p>
        <p>
          <span>
            GreenRing, Inc (“GreenRing”, “us”, “we”, or “our”) operates an online platform that facilitates the matching
            of individuals or entities (each, a “Customer”) desiring to subscribe for access (a “Subscription”) to
            certain content on social media accounts of individuals or entities (“Influencers”), which may include,
            without limitation, Instagram, Twitter, Facebook and Snapchat (collectively, “Social Media Platforms”).
          </span>
        </p>
        <p>
          <span>
            The following terms of use (“Terms of Use”) govern your access to and use of: (i) the GreenRing website
            located at&nbsp;
          </span>
          <a href="http://www.greenring.co" aria-describedby="a11y-external-message">
            <span>www.greenring.co</span>
          </a>
          <span>
            &nbsp;(the “Website”), (ii) the GreenRing online platform and mobile application, and (iii) all other
            services and content provided by GreenRing, as described on the Website or mobile application (collectively,
            the “Platform”). These Terms of Use form an agreement between GreenRing and you. By accessing and using the
            Platform, you accept and agree to be bound by and comply with these Terms of Use. If you do not accept and
            agree to be bound by these Terms of Use, please do not access or use the Platform.
          </span>
        </p>
        <p>
          <span>
            As a condition of your use of the Platform, you represent and warrant that: (i) you have reached the age of
            majority in your jurisdiction of residence; (ii) if you are accessing or using the Platform on behalf of
            another person or entity, you have the authority to bind such person or entity to these Terms of Use; (iii)
            you will use the Platform in accordance with these Terms of Use; and (iv) all information supplied by you on
            or through the Platform is true, accurate, current and complete.
          </span>
        </p>
        <p>
          <span>
            The Platform is provided solely to (i) assist Customers in entering into agreements with Influencers to gain
            access to content on Social Media Platforms that requires an acceptance by such Influencers for the Customer
            to access, in each case as listed and available on the Platform (each, an “Influencer Profile”), and (ii)
            facilitate the transmission of payments from Customers to Influencers pursuant to such agreements.
          </span>
        </p>
        <p>
          <b>Changes to Terms of Use and Platform:</b>
        </p>
        <p>
          <span>
            Except where prohibited by applicable law, GreenRing reserves the right to change these Terms of Use at any
            time without notice. Your continued access to or use of the Platform after any changes to these Terms of Use
            indicates your acceptance of such changes. It is your responsibility to review these Terms of Use regularly.
          </span>
        </p>
        <p>
          <span>
            GreenRing reserves the right to change or remove any information, material or content (including, but not
            limited to, price, features, availability of Influencers, Influencer Profiles, and types of Subscriptions)
            contained on or provided through the Platform at any time, and from time to time, without notice.
          </span>
        </p>
        <p>
          <b>Accounts:</b>
        </p>
        <p>
          <span>
            By creating an account on the Platform, you agree to provide true, accurate, current, and complete
            information. You agree not to create a Platform account using a false identity or providing false
            information or if you have previously been removed or banned from the Platform. You are responsible for
            maintaining the confidentiality of the Platform account information, including your username and password.
            You are responsible for all activities that occur on or in connection with your Platform account and you
            agree to notify us immediately of any unauthorized access or use of your Platform account. You acknowledge
            and agree that we are not responsible or liable for any damages, losses, costs, expenses, or liabilities
            related to any unauthorized access to or use of your Platform account. You further acknowledge that
            GreenRing reserves the right to suspend, disable or terminate any account in its sole and absolute
            discretion.
          </span>
        </p>
        <p>
          <b>Main Terms:</b>
        </p>
        <p>
          <b>Customer Terms:</b>
        </p>
        <p>
          <span>The following terms apply to Customers on the Platform:</span>
        </p>
        <p>
          <span>Subscription Fees and Payment</span>
        </p>
        <ol>
          <li>
            <span>
              GreenRing brokers Subscriptions between Customers and Influencers in order to grant Customers access to
              Influencer Profiles on a monthly basis. Specific pricing for Subscriptions to be described on the
              Platform. Any prices quoted will not include any additional data, internet or other telecommunication
              charges that may be charged by your internet or mobile provider. You are solely responsible for any such
              charges.
            </span>
          </li>
          <li>
            <span>
              If you purchase a Subscription, you agree to pay all fees and charges associated with that Subscription on
              a timely basis. Unless otherwise stated, all fees and charges are due and payable in advance and are
              non-refundable. All such fees and charges will be charged on each 30-day anniversary from the day your
              Subscription was activated to the payment method you provided when you purchased the Subscription.
            </span>
          </li>
          <li>
            <span>
              You agree to maintain a valid payment method during the term of any Subscription. Failure to do so may
              lead to a cancellation of your Subscription.
            </span>
          </li>
          <li>
            <span>
              All fees will be charged without renewal notices unless and until you cancel by following the steps
              outlined below. To see your next payment date, visit your original payment receipt or contact the Company
              at&nbsp;support@greenring.co
            </span>
          </li>
          <li>
            <span>
              YOU ACKNOWLEDGE AND AGREE THAT EACH SUBSCRIPTION AUTOMATICALLY RENEWS, AND YOU WILL CONTINUE TO BE
              CHARGED, UNLESS AND UNTIL YOU CANCEL YOUR SUBSCRIPTION, OR WE SUSPEND OR TERMINATE IT, IN ACCORDANCE WITH
              THESE TERMS OF USE.
            </span>
          </li>
          <li>
            <span>
              When you initially purchase a Subscription, your credit card or other payment method will be authorized
              for the amount of the Subscription but will only be charged once you have been granted access to the
              applicable Influencer Profile. Please allow up to 5 business days after the purchase of a Subscription for
              your access to the Influencer Profile to be granted. If you do not receive access to an Influencer Profile
              within 5 business days after the purchase of your Subscription, your credit card or other payment method
              will not be charged and your Subscription purchase will be cancelled.
            </span>
          </li>
          <li>
            <span>
              We reserve the right to change your monthly Subscription fee at any time in our sole discretion; however,
              any price changes will not be implemented until your next monthly Subscription period and we will always
              give you reasonable notice prior to such implementation. If you do not agree to the new fee, you can
              cancel your Subscription before your next monthly Subscription period by following the cancellation steps
              set forth below. If you do not cancel, you will be charged the new fee commencing with the next monthly
              Subscription period.
            </span>
          </li>
          <li>
            <span>
              GreenRing may reject, suspend or terminate your Subscription at any time in its sole discretion.
            </span>
          </li>
        </ol>
        <p>
          <span>Customer Cancellation and Refund Policy</span>
        </p>
        <ol>
          <li>
            <span>Subscriptions may be cancelled by you at any time, subject to these Terms of Use.&nbsp;</span>
          </li>
          <li>
            <span>
              Cancelling a Subscription does not provide you a refund for Subscription payments already processed, nor
              does it stop a pending charge. Your cancellation will go into effect for the following billing cycle.
            </span>
          </li>
          <li>
            <span>
              No refunds will be given on Subscriptions that have been completed incorrectly due to Customer error (such
              as forgetting to include your social media username or spelling your username incorrectly).
            </span>
          </li>
          <li>
            <span>
              Fees may be refunded on a case-by-case basis at GreenRing’s sole discretion, and may be subject to a $5
              refund processing fee.
            </span>
          </li>
        </ol>
        <p>
          <span>Information</span>
        </p>
        <ol>
          <li>
            <span>
              You acknowledge that information provided by you on the Platform may be shared with Influencers and their
              representatives.
            </span>
          </li>
          <li>
            <span>
              Influencers may require additional information before granting access to an Influencer Profile. Neither
              GreenRing nor any Influencers will be responsible for any delay in granting access to a Subscription that
              is due to your failure to respond to a reasonable request for additional information.
            </span>
          </li>
          <li>
            <span>
              From time to time we may send you communications to the email address associated with your account. These
              communications may include, but are not limited to, Influencer recommendations, special offers and other
              account-related or transactional messages.
            </span>
          </li>
        </ol>
        <p>
          <b>Influencer Terms:</b>
        </p>
        <p>
          <span>The following terms apply to Influencers on the Platform:</span>
        </p>
        <p>
          <span>The Program</span>
        </p>
        <ol>
          <li>
            <span>
              The GreenRing Influencer program (the “Program”) is designed to enable Influencers to charge a monthly
              recurring fee to Customers for access to Influencer Profiles. You will sign up for an account through the
              Platform and indicate the Influencer Profile(s) that will be available for Subscriptions on the Platform.
              You must own, have administrative privileges and/or have proper publicist relations on behalf of the
              Influencer and Influencer Profile that are the subject of any Subscription offering.
            </span>
          </li>
          <li>
            <span>
              You will provide access to the applicable Influencer Profile to all Customers who purchase a Subscription
              through the Platform, as communicated to you by GreenRing, and verify its completion. You must notify
              GreenRing within 72 hours of receiving notice of a Subscription if you are unable to grant a Customer
              access to the applicable Influencer Profile or if a Subscription conflicts with your content guidelines
              and policies. If you fail to add Customers to an Influencer Profile on more than three consecutive
              Subscription orders, you may be subject to removal or suspension from the Program.
            </span>
          </li>
          <li>
            <span>
              You will post a minimum of one (1) post on average to each applicable Influencer Profile per day.
            </span>
          </li>
          <li>
            <span>
              You are not allowed to solicit Customers outside of GreenRing regarding payments for Subscriptions on any
              Influencer Profile. All payments are handled by GreenRing. Compensation for approved Subscriptions will
              only be credited if the order is completed through the Platform. During Influencer’s participation in the
              Program, Influencer will not grant any other person or entity the right to monetize subscriptions to any
              applicable Influencer Profile subject to the Program. If you violate the terms of this paragraph, you will
              be liable to GreenRing for its proportional percentage of any fees that would otherwise be due to
              GreenRing, in addition to any other rights and remedies of GreenRing at law or in equity.
            </span>
          </li>
          <li>
            <span>
              You must abide by all laws and policies applicable to any Influencer Profile, including any rules and
              policies of the Social Media Platforms on which the Influencer Profile is maintained. If an Influencer
              Profile is suspended or terminated in any manner, including by any Social Media Platform, we may issue
              refunds to Customers with Subscriptions to such Influencer Profile, in our sole discretion, and such
              amounts may be appropriately deducted from Influencer Revenue (as defined below).
            </span>
          </li>
          <li>
            <span>
              If you violate these Terms of Use, we may suspend or terminate your account and you may be required to
              forfeit any balance in your account.
            </span>
          </li>
        </ol>
        <p>
          <span>Term and Termination</span>
        </p>
        <p>
          <span>
            Influencer or GreenRing may terminate Influencer’s participation in the Program for any reason upon written
            notice to the other party, with immediate effect. Notice to GreenRing for such purpose can be sent to{' '}
          </span>
          <a href="mailto:support@greenring.co" aria-describedby="a11y-external-message">
            <span>support@greenring.co</span>
          </a>
          <span>
            . Upon any such termination, Influencer shall maintain any Subscribers’ access to any Influencer Profile for
            the duration of any then-outstanding Subscription periods. We have the right, in our sole discretion as we
            deem necessary to maintain the integrity of the Platform, to refund Subscription payments made by any
            Customers to the Platform, even if Influencer maintains its duties to such Customers for the duration of any
            ongoing Subscriptions following termination of Influencer’s participation in the Program.
          </span>
        </p>
        <p>
          <span>Payments</span>
        </p>
        <ol>
          <li>
            <span>
              GreenRing will pay Influencer seventy percent (70%) of all fees actually received from Customers for
              Subscriptions to your Influencer Profiles procured through the Platform, less any chargebacks, Customer
              disputed amounts or refunds (“Influencer Revenue”). All credit card processing fees shall be added on top
              of Subscription fees and thus shall not be deducted from the calculation of Influencer Revenue.
            </span>
          </li>
          <li>
            <span>
              You will be granted access to a real-time dashboard specific to your Subscriptions to allow you to track
              all revenue and accounting related to your Subscriptions.
            </span>
          </li>
          <li>
            <span>
              Payments will be made by GreenRing on or before the 1st day (or next successive business day if a weekend
              or bank holiday) of the month following the date which is 30 days after the end of a Subscription period.
              For example, if a Customer’s monthly Subscription period ends on May 25, payment for such monthly
              Subscription period will be paid by July 1 (or next successive business day). Payments will be made to an
              account designated by Influencer in writing.
            </span>
          </li>
          <li>
            <span>
              Upon termination of the Program, or termination of Influencer’s participation in the Program by either
              GreenRing or Talent, GreenRing shall have no further obligations to Influencer, except for the payment of
              undisputed Influencer Revenue generated on the Platform prior to the date of termination.
            </span>
          </li>
        </ol>
        <p>
          <span>Confidentiality</span>
        </p>
        <p>
          <span>
            Influencer shall not disclose any Confidential Information to any third party, other than Influencer’s
            representatives who have a need to know such Confidential Information in connection with the services
            provided hereunder. Influencer shall be responsible for any unauthorized use or disclosure of Confidential
            Information by such representatives. Further, Influencer agrees to use the Confidential Information solely
            in connection with the current or contemplated business relationship between the parties. No other right or
            license, whether expressed or implied in the Confidential Information is granted to Influencer hereunder,
            and all rights to Confidential Information shall remain with us. All use of Confidential Information shall
            be for the benefit of us and the current or contemplated business relationship between the parties. All such
            confidentiality obligations shall survive the termination of your participation on the Program. For purposes
            of these Terms of Use, “Confidential Information” means any data, business models, plans, strategies or
            other information regarding us that is not generally known to the public.
          </span>
        </p>
        <p>
          <b>License Grants</b>
        </p>
        <p>
          <span>
            Subject to these Terms of Use, GreenRing grants you a personal, revocable, non-exclusive and
            non-transferable license to access and use the Platform and the services offered on the Platform (the
            “Platform Services”).
          </span>
        </p>
        <p>
          <span>
            Subject to these Terms of Use, you grant to GreenRing a perpetual, transferrable, irrevocable, royalty-free,
            fully paid-up, worldwide, and fully sublicensable license to access, collect, store and use any data,
            information, records and files that (1) you load, transmit to, or enter into the Platform, or (2) we collect
            from your local computer system or from third-parties with your permission, and (in each case) including all
            results from processing such data, including compilations, and derivative works thereof solely for the
            purpose of: (A) providing the Platform Services, (B) complying with applicable law, and (C) GreenRing’s
            reasonable audit and data retention policies.
          </span>
        </p>
        <p>
          <b>Effects of Term and Termination</b>
        </p>
        <p>
          <span>
            Upon any termination, discontinuation, or cancellation of your access to the Platform or your account, all
            provisions of these Terms of Use which by their nature should survive will survive, including, without
            limitation, ownership provisions, warranty disclaimers, limitations of liability, and dispute resolution
            provisions.
          </span>
        </p>
        <p>
          <b>Ownership</b>
        </p>
        <p>
          <span>
            All content provided or displayed by GreenRing through the Platform, including (without limitation) all
            designs, infrastructure, graphics, pictures, illustrations, software, artwork, video, music, sound, names,
            words, titles, phrases, logos and marks (the “Platform Content”), are owned or licensed by GreenRing and are
            protected by copyright, trade-mark and other intellectual property laws.
          </span>
        </p>
        <p>
          <span>
            GreenRing expressly reserves all rights in the Platform, the Platform Services, and the Platform Content
            that are not specifically granted to you. You acknowledge that all right, title and interest in the
            Platform, the Platform Services, the Platform Content, and any update, adaptation, translation,
            customization or derivative work thereof, and all intellectual property rights therein will remain with
            GreenRing (or third party suppliers, if applicable), and that the Platform, Platform Services, and Platform
            Content are licensed and not “sold” to you.
          </span>
        </p>
        <p>
          <b>Privacy Policy</b>
        </p>
        <p>
          <span>Please visit&nbsp;</span>
          <a href="https://soclose.com/privacy" aria-describedby="a11y-external-message">
            <span>greenring.co/privacy</span>
          </a>
          <span>
            &nbsp;to review our current Privacy Policy, which contains important information about our practices in
            collecting, storing, using and disclosing your personal information, and which is hereby incorporated into
            and forms a part of these Terms of Use.
          </span>
        </p>
        <p>
          <b>No Unlawful or Prohibited Use</b>
        </p>
        <p>
          <span>
            You shall not, without GreenRing’s prior written permission, use the Platform for purposes other than
            accessing and using the Platform Services. Without limiting the generality of the foregoing, you shall not,
            and shall not permit anyone else to:
          </span>
        </p>
        <p>
          <span>
            (a) “frame,” “mirror,” or otherwise incorporate the Platform or the Platform Content or any part thereof on
            any commercial or non-commercial website;
          </span>
        </p>
        <p>
          <span>
            (b) access, monitor, or copy any part of the Platform or the Platform Content using any robot, spider,
            scraper, or other automated means or any manual process for any purpose without our express written
            permission;
          </span>
        </p>
        <p>
          <span>
            (c) violate the restrictions in any robot exclusion headers on the Platform Content or the Platform or
            bypass or circumvent other measures employed to prevent or limit access to the Platform;
          </span>
        </p>
        <p>
          <span>
            (d) take any action that imposes, or may impose, in GreenRing’s discretion, an unreasonable or
            disproportionately large load on the Platform;
          </span>
        </p>
        <p>
          <span>(e) deep-link to any portion of the Platform for any purpose;</span>
        </p>
        <p>
          <span>
            (f) remove (or permit anyone else to remove) any watermarks, labels, or other legal or proprietary notices
            included in any Campaign, the Platform, or the Platform Content;
          </span>
        </p>
        <p>
          <span>
            (g) modify or attempt to modify (or permit anyone else to modify or attempt to modify) the Platform or any
            scheduled Campaigns, including any modification for the purpose of disguising or changing any indications of
            the ownership or source of the Platform or a Campaign;
          </span>
        </p>
        <p>
          <span>
            (i) attempt to, or assist, authorize or encourage others to, circumvent, disable or defeat any of the
            security features or components, such as digital rights management software or encryption, that protect the
            Platform;
          </span>
        </p>
        <p>
          <span>
            (j) copy, reproduce, modify, distribute, transfer, sell, publish, broadcast, perform, transmit, publish,
            license or circulate in any form any part of the Platform or the Platform Content;
          </span>
        </p>
        <p>
          <span>
            (k) create derivative works based on the Platform or the Platform Content, in whole or in part, or to
            decompile, disassemble, reverse engineer or otherwise exploit any part of the Platform;
          </span>
        </p>
        <p>
          <span>
            (l) use or access the Platform in a manner that violates the rights (including, but not limited to
            intellectual property rights) of any third party; or
          </span>
        </p>
        <p>
          <span>
            (n) post, upload, publish, submit or transmit any content on an Influencer Profile that, in GreenRing’s sole
            discretion:
          </span>
        </p>
        <p>
          <span>
            (i) infringes, misappropriates or violates a third party’s patent, copyright, trademark, trade secret, moral
            rights or other intellectual property rights, or rights of publicity or privacy;
          </span>
        </p>
        <p>
          <span>
            (ii) violates, or encourages any conduct that would violate, any applicable law or regulation or would give
            rise to civil liability;
          </span>
        </p>
        <p>
          <span>(iii) is fraudulent, false, misleading, or deceptive;</span>
        </p>
        <p>
          <span>(iv) is defamatory, obscene, pornographic, vulgar, or offensive;</span>
        </p>
        <p>
          <span>
            (v) promotes discrimination, bigotry, racism, hatred, harassment, or harm against any individual or group;
          </span>
        </p>
        <p>
          <span>
            (vi) is violent or threatening or promotes violence or actions that are threatening to any person or entity;
            or
          </span>
        </p>
        <p>
          <span>(vii) promotes illegal or harmful activities or substances.</span>
        </p>
        <p>
          <span>
            You shall be solely liable for any damages resulting from any violation of the foregoing restrictions, or
            any other harm resulting from your use of the Platform or your operation of any Influencer Profile.
          </span>
        </p>
        <p>
          <b>Third Party Websites</b>
        </p>
        <p>
          <span>
            GreenRing may contract with third-party service providers for certain services, such as payment services,
            and may also provide links to other third-party websites that are not under the control of GreenRing. In
            addition to these Terms of Use, you may be bound by any additional terms required by the third-party service
            providers. GreenRing makes no representations about any other website to which you may have access through
            the Platform, and accepts no liability for activities or content on third-party sites. GreenRing is not
            responsible for the accuracy or reliability of any information, data, opinions, advice, or statements made
            on third-party sites or for the quality of any products or services available on such sites.
          </span>
        </p>
        <p>
          <span>
            GreenRing may enable you to link your account with a valid account on a third party social networking,
            email, or content service such as Instagram, Facebook, YouTube, or Twitter, (such service, a “Third-Party
            Service” and each such account, a “Third-Party Account”) by allowing GreenRing to access your Third-Party
            Account, as permitted under the applicable terms and conditions that govern your use of each Third-Party
            Account (“Third-Party Terms”). You represent and warrant that you are entitled to disclose your Third-Party
            Account information to GreenRing and/or grant GreenRing access to your Third-Party Account (including, but
            not limited to, for use for the purposes described herein), and that such disclosure or access will not
            violate the applicable Third-Party Terms, obligate GreenRing to pay any fees, or make GreenRing subject to
            any usage limitations imposed by such Third-Party Service providers.
          </span>
        </p>
        <p>
          <span>
            By granting GreenRing access to any Third-Party Accounts, you understand that GreenRing may access, make
            available and store (if applicable) any information, data, text, software, music, sound, photographs,
            graphics, video, messages, tags and/or other materials that you have provided to and/or stored in your
            Third-Party Account (“TPS Content”) so that it is available on and through the Platform via your account.
            Depending on the Third-Party Accounts you choose and subject to the privacy settings that you have set in
            such Third-Party Accounts, personally identifiable information that you post to your Third-Party Accounts
            may be available on and through your account on the Platform. You hereby authorize GreenRing to retrieve
            information from, and submit information to, such Third Party Services at your request, and to provide
            Platform Services relating to any information we retrieve from those Third-Party Accounts. Please note that
            if a Third-Party Account or associated service becomes unavailable or GreenRing access to such Third-Party
            Account is terminated by the Third-Party Service provider, then TPS Content will no longer be available on
            and through the Platform. You have the ability to disable the connection between your account and your
            Third-Party Accounts at any time by accessing the “Settings” section. In most cases, we do not store the
            entirety of your TPS Content, but instead we collect and store the metadata associated with such TPS
            Content. You hereby authorize such collection and storage.
          </span>
        </p>
        <p>
          <b>Relationship with Third-Party Services.</b>
        </p>
        <p>
          <span>
            PLEASE NOTE THAT YOUR RELATIONSHIP WITH THIRD-PARTY SERVICE PROVIDERS IS GOVERNED SOLELY BY YOUR
            AGREEMENT(S) WITH SUCH THIRD-PARTY SERVICE PROVIDERS, AND GreenRing DISCLAIMS ANY LIABILITY FOR PERSONALLY
            IDENTIFIABLE INFORMATION THAT MAY BE PROVIDED TO IT BY SUCH THIRD-PARTY SERVICE PROVIDERS IN VIOLATION OF
            THE PRIVACY SETTINGS THAT YOU HAVE SET IN SUCH THIRD-PARTY ACCOUNTS. GreenRing makes no effort to review TPS
            Content for any purpose, including but not limited to, for accuracy, legality or non-infringement, and
            GreenRing is not responsible for any TPS Content. You should note that a Third-Party Service may change or
            amend its guidelines and our access to it at any time, and we cannot guarantee that the Platform Services
            will always include a connection to such Third Party Service.
          </span>
        </p>
        <p>
          <b>Communications Not Confidential</b>
        </p>
        <p>
          <span>
            GreenRing does not guarantee the confidentiality of any communications made by you through the Platform.
            Although GreenRing generally adheres to the accepted industry practices in securing the transmission of data
            to, from, and through the Platform, you agree and acknowledge that GreenRing cannot and does not guarantee
            the security of data transmitted over the Internet or public networks.
          </span>
        </p>
        <p>
          <b>DISCLAIMER OF WARRANTIES</b>
        </p>
        <p>
          <span>
            GreenRing cannot and does not guarantee or warrant that the Platform or Platform Services, or any links from
            the Platform, will be free of viruses or other destructive code. You are responsible for implementing
            sufficient procedures and checkpoints to satisfy your particular requirements for anti-virus protection and
            accuracy of data input and output, and for maintaining a means external to the Platform for any
            reconstruction of any lost data. GreenRing WILL NOT BE LIABLE FOR ANY LOSS OR DAMAGE CAUSED BY A DISTRIBUTED
            DENIAL-OF-SERVICE ATTACK, VIRUSES, OR OTHER TECHNOLOGICALLY HARMFUL MATERIAL THAT MAY INFECT YOUR COMPUTER
            EQUIPMENT, COMPUTER PROGRAMS, DATA, OR OTHER PROPRIETARY MATERIAL DUE TO YOUR USE OF AND ACCESS TO THE
            PLATFORM AND PLATFORM SERVICES.
          </span>
        </p>
        <p>
          <span>
            YOUR USE OF THE PLATFORM AND PLATFORM SERVICES IS AT YOUR OWN RISK. THE PLATFORM AND PLATFORM SERVICES ARE
            PROVIDED ON AN “AS IS” AND “AS AVAILABLE” BASIS, WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR
            IMPLIED, STATUTORY OR OTHERWISE, INCLUDING BUT NOT LIMITED TO ANY WARRANTIES OF MERCHANTABILITY,
            NON-INFRINGEMENT AND FITNESS FOR PARTICULAR PURPOSE. NEITHER GreenRing NOR ANY PERSON ASSOCIATED WITH
            GreenRing MAKES ANY WARRANTY OR REPRESENTATION WITH RESPECT TO THE COMPLETENESS, SECURITY, RELIABILITY,
            QUALITY, ACCURACY, OR AVAILABILITY OF THE PLATFORM OR PLATFORM SERVICES. WITHOUT LIMITING THE FOREGOING,
            NEITHER GreenRing NOR ANYONE ASSOCIATED WITH GreenRing REPRESENTS OR WARRANTS THAT THE PLATFORM OR PLATFORM
            SERVICES WILL BE ACCURATE, RELIABLE, ERROR-FREE (INCLUDING PRICING ERRORS), OR UNINTERRUPTED, THAT DEFECTS
            WILL BE CORRECTED, THAT THE PLATFORM OR THE SERVER THAT MAKES IT AVAILABLE ARE FREE OF VIRUSES OR OTHER
            HARMFUL COMPONENTS, OR THAT THE PLATFORM OR PLATFORM SERVICES WILL OTHERWISE MEET YOUR NEEDS OR
            EXPECTATIONS. NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN, OBTAINED BY YOU FROM GreenRing OR THROUGH
            OR FROM THE PLATFORM WILL CREATE ANY WARRANTY NOT EXPRESSLY STATED IN THESE TERMS.
          </span>
        </p>
        <p>
          <span>
            GreenRing MAKES NO GUARANTEES OR WARRANTIES ABOUT ANY INFLUENCER PROFILES OR ANY CONTENT POSTED (OR NOT
            POSTED) THEREON. CUSTOMERS SHALL HAVE NO RECOURSE AGAINST GreenRing WHATSOEVER WITH RESPECT TO ANY
            INFLUENCER PROFILE OR CONTENT THEREON.
          </span>
        </p>
        <p>
          <span>
            THE FOREGOING DOES NOT AFFECT ANY WARRANTIES WHICH CANNOT BE EXCLUDED OR LIMITED UNDER APPLICABLE LAW.
          </span>
        </p>
        <p>
          <b>LIMITATION OF LIABILITY</b>
        </p>
        <p>
          <span>
            IN NO EVENT WILL GreenRing OR ANY PERSON ASSOCIATED WITH GreenRing BE LIABLE, WHETHER BASED ON WARRANTY,
            CONTRACT, TORT, NEGLIGENCE, STRICT LIABILITY OR ANY OTHER LEGAL THEORY, FOR ANY DAMAGES OF ANY KIND
            (INCLUDING, WITHOUT LIMITATION, DIRECT, INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, EXEMPLARY OR PUNITIVE
            DAMAGES, LOST PROFITS, LOSS OF USE, LOSS OF DATA, PERSONAL INJURY, FINES, FEES, PENALTIES OR OTHER
            LIABILITIES), WHETHER OR NOT GreenRing IS ADVISED OF THE POSSIBILITY OF SUCH DAMAGES, RESULTING FROM OR
            RELATED TO THE USE OF, OR THE INABILITY TO MAKE USE OF, THE PLATFORM, PLATFORM SERVICES OR PLATFORM CONTENT.
          </span>
        </p>
        <p>
          <span>
            INFLUENCERS AND CUSTOMERS ARE INDEPENDENT PARTIES AND NOT CONTRACTORS, AGENTS OR EMPLOYEES OF GreenRing.
            GreenRing IS NOT LIABLE FOR THE ACTS, ERRORS, OMISSIONS, REPRESENTATIONS, WARRANTIES, BREACHES OR NEGLIGENCE
            OF ANY INFLUENCER OR CUSTOMER OR FOR ANY PERSONAL INJURIES, DEATH, PROPERTY DAMAGE, OR OTHER DAMAGES OR
            EXPENSES RESULTING THEREFROM AND TAKES NO RESPONSIBILITY WHATSOEVER RELATING TO ANY
          </span>
        </p>
        <p>
          <span>
            SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR LIMITATION OF LIABILITY FOR CONSEQUENTIAL OR INCIDENTAL
            DAMAGES, SO THE ABOVE LIMITATION MAY NOT APPLY TO YOU. TO THE EXTENT THAT THE FOREGOING LIMITATION DOES NOT
            APPLY, IN NO EVENT WILL THE TOTAL AGGREGATE LIABILITY OF GreenRing IN CONNECTION WITH YOUR USE OF OR
            INABILITY TO USE THE PLATFORM OR FOR ANY OTHER CLAIM RELATED IN ANY WAY TO THE PLATFORM, EXCEED ONE UNITED
            STATES DOLLAR ($1) OR THE EQUIVALENT IN LOCAL CURRENCY. THE EXISTENCE OF ONE OR MORE CLAIMS UNDER THESE
            TERMS OF USE WILL NOT INCREASE THE MAXIMUM LIABILITY AMOUNT.
          </span>
        </p>
        <p>
          <span>
            The limitations above reflect the allocation of risk between the parties. The limitations specified in this
            section will survive and apply even if any limited remedy specified in these terms is found to have failed
            of its essential purpose.
          </span>
        </p>
        <p>
          <b>Limitations Period</b>
        </p>
        <p>
          <span>
            YOU AND GreenRing AGREE THAT ANY CAUSE OF ACTION ARISING OUT OF OR RELATED TO THESE TERMS OF USE, THE
            PLATFORM, OR THE PLATFORM SERVICES MUST COMMENCE WITHIN THREE (3) MONTHS AFTER THE CAUSE OF ACTION ACCRUES.
            OTHERWISE, SUCH CAUSE OF ACTION IS PERMANENTLY BARRED.
          </span>
        </p>
        <p>
          <b>Binding Arbitration</b>
        </p>
        <p>
          <span>
            Any controversy or claim arising out of or relating to these Terms of Use or any related agreement, their
            enforcement or interpretation, or because of an alleged breach, default or misrepresentation in connection
            with any of their provisions, shall be determined by binding arbitration. The arbitration proceedings shall
            be held and conducted by a single arbitrator in accordance with the Comprehensive Arbitration Rules and
            Procedures of JAMS (the “JAMS Rules”), as modified by these Terms of Use. Such arbitration shall occur in
            Los Angeles, California, and be initiated by any party in accordance with the JAMS Rules. The demand for
            arbitration shall be made by any party hereto within a reasonable time after the claim, dispute or other
            matter in question has arisen, and in any event shall not be made after the date when institution of legal
            proceeding, based on such claim, dispute or other matter in question, would be barred by the applicable
            statute of limitations. Discovery issues shall be decided by the arbitrator. Post-hearing briefs shall be
            permitted. The arbitrator shall render a decision within twenty (20) days after the conclusion of the
            hearing(s). In reaching a decision, the arbitrator shall have no authority to change, extend, modify or
            suspend any of the terms of this Agreement, or to grant an award or remedy any greater than that which would
            be available from a court under the statutory or common law theory asserted. The arbitrator shall issue a
            written opinion that includes the factual and legal basis for any decision and award. The arbitrator shall
            apply the substantive law (and the law of remedies, if applicable) of California or federal law, or any of
            them, as applicable to the claim(s) asserted. Judgment on the award may be entered in any court of competent
            jurisdiction. The parties may seek, from a court of competent jurisdiction, provisional remedies or
            injunctive relief in support of their respective rights and remedies hereunder without waiving any right to
            arbitration. However, the merits of any action that involves such provisional remedies or injunctive relief,
            including, without limitation, the terms of any permanent injunction, shall be determined by arbitration
            under this paragraph. Each party will bear its own respective costs and expenses (including legal fees and
            expenses and the cost of arbitration) in any such arbitration proceeding.
          </span>
        </p>
        <p>
          <span>
            You agree to pursue any arbitration in an individual capacity and not as class representative or class
            member in any purported class action proceeding.
          </span>
        </p>
        <p>
          <span>
            BY AGREEING TO ARBITRATE DISPUTES, THE PARTIES HEREBY ACKNOWLEDGE AND AGREE THAT THEY ARE IRREVOCABLY
            WAIVING ANY AND ALL RIGHT TO TRIAL BY JURY IN ANY LEGAL PROCEEDING ARISING OUT OF OR RELATING TO THESE TERMS
            OF USE OR THE ACTIVITIES CONTEMPLATED HEREBY.
          </span>
        </p>
        <p>
          <b>Indemnification</b>
        </p>
        <p>
          <span>
            You shall defend, indemnify, and hold harmless GreenRing and all of its officers, directors, employees and
            agents from and against any claims, causes of action, demands, recoveries, losses, damages, fines, penalties
            or other costs or expenses of any kind or nature including but not limited to reasonable legal and
            accounting fees, brought in connection with or as a result of: (a) your breach of any of your warranties,
            representations, or obligations under these Terms of Use or any documents referenced herein; (b) your
            violation of any law or regulation (including without limitation any FTC requirements or guidelines) or the
            rights of a third party (including, without limitation, intellectual property rights); (c) your use of the
            Platform; or (d) with respect to an Influencer, your operations of your Influencer Profiles.
          </span>
        </p>
        <p>
          <b>Geographic Application of the Platform</b>
        </p>
        <p>
          <span>
            Not all of the Influencers, Subscriptions, and Platform Services are available in all jurisdictions.
            Furthermore, nothing on the Platform constitutes an offer or solicitation to buy or sell any product or
            service to anyone in any jurisdiction in which such an offer or solicitation is prohibited by law.
          </span>
        </p>
        <p>
          <b>Governing Law</b>
        </p>
        <p>
          <span>
            These Terms of Use and any action related thereto shall be governed by the laws of the State of California
            without regard to its conflict of laws provisions. These laws apply to your access to or use of the Platform
            or Platform Services, notwithstanding your domicile, residency or physical location. The Platform and the
            Platform Services are intended for use only in jurisdictions where they may lawfully be offered for use.
            Except as restricted by applicable law, you hereby consent to the exclusive jurisdiction and venue of the
            state and federal courts located in Los Angeles, California in all disputes arising out of or relating to
            the use of the Platform or the Platform Services.
          </span>
        </p>
        <p>
          <b>Entire Agreement, Waiver and Severability</b>
        </p>
        <p>
          <span>
            These Terms of Use constitute the entire agreement between GreenRing and you pertaining to the subject
            matter hereof and supersede all prior or contemporaneous communications and proposals, whether electronic,
            oral or written, between you and GreenRing with respect to the Platform.
          </span>
        </p>
        <p>
          <span>
            GreenRing’s failure to insist upon or enforce strict performance of any provision of these Terms of Use
            shall not be construed as a waiver of any provisions or right. The waiver of any such right or provision
            will be effective only if in writing and signed by a duly authorized representative of GreenRing. Except as
            expressly set forth in these Terms of Use, the exercise by either party of any of its remedies under these
            Terms of Use will be without prejudice to its other remedies under these Terms of Use or otherwise.
          </span>
        </p>
        <p>
          <span>
            If for any reason a court of competent jurisdiction finds any provision of these Terms of Use invalid or
            unenforceable, that provision will be enforced to the maximum extent permissible and the other provisions of
            these Terms of Use will remain in full force and effect.
          </span>
        </p>
        <p>
          <span>
            If any of the provisions contained in these Terms of Use conflict with the terms of another agreement
            between the parties, then these Terms of Use shall prevail.
          </span>
        </p>
      </div>
    </MainLayout>
  );
};

export default TermsConditions;
